// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { createTokenRequest } from "../authConfig";
import { logAuthFailure } from "../utils/authDiagnostics";
import {
  attemptInteractiveReauth,
  isInteractiveRedirectInFlight,
  isInteractionRequired,
} from "../utils/interactiveReauth";
import {
  notifySessionExpired,
  useSessionExpiredSignal,
} from "../utils/sessionExpirySignal";

export function useSessionValidation(): boolean {
  const { instance, accounts } = useMsal();
  const account = accounts[0] ?? null;
  const expired = useSessionExpiredSignal();
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (account === null || expired || checked) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        await instance.acquireTokenSilent({
          ...createTokenRequest(),
          account,
        });
        if (!cancelled) {
          setChecked(true);
        }
      } catch (error: unknown) {
        if (isInteractionRequired(error)) {
          if (
            attemptInteractiveReauth(instance, account, createTokenRequest()) ||
            isInteractiveRedirectInFlight()
          ) {
            logAuthFailure("session-validation-needs-interactive-hop", error);
            return;
          }
        }

        logAuthFailure("session-validation-expired", error);
        notifySessionExpired();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [account, expired, checked, instance]);

  return checked;
}
